/**
 * Client Call Type for client to make call w.r.t. audio, video, presentation direction
 */

export enum ClientCallType {
    /**
     * Client to join as a control-only participant, i.e. the
     * user will not send or receive any audio or video. They
     * can still access the conference controls.
     */
    None = 0,
    /**
     * Join as a sending-audio-only participant, not receiving
     * any audio, also will not send or receive video.
     */
    AudioSend = 1 << 1,
    /**
     * Join as a receiving-audio-only participant, not sending
     * any audio, also will not send or receive video.
     */
    AudioRecv = 1 << 2,
    /**
     * Join as a sending-video-only participant, not receiving
     * any video, also will not send or receive audio.
     */
    VideoSend = 1 << 3,
    /**
     * Join as a receiving-video-only participant, not sending
     * any video, also will not send or receive audio.
     */
    VideoRecv = 1 << 4,
    /**
     * Join as a sending-presentation-only participant.
     */
    PresentationSend = 1 << 5,
    /**
     * Join as a receiving-presentation-only participant.
     */
    PresentationRecv = 1 << 6,
    /**
     * Join as an audio-only participant, i.e. the user will send and receive
     * audio but will not send or receive video.
     */
    AudioSendRecv = AudioSend | AudioRecv,
    /**
     * Join as a video-only participant, i.e. the user will send and receive
     * video but will not send or receive audio.
     */
    VideoSendRecv = VideoSend | VideoRecv,
    /**
     * Join as a presentation only participant
     */
    PresentationSendRecv = PresentationSend | PresentationRecv,
    AudioRecvPresentationSendRecv = AudioRecv | PresentationSendRecv,
    AudioSendPresentationSendRecv = AudioSend | PresentationSendRecv,
    VideoRecvPresentationSendRecv = VideoRecv | PresentationSendRecv,
    VideoSendPresentationSendRecv = VideoSend | PresentationSendRecv,
    /**
     * Join as a sending-audio-video-only participant, i.e. the user will only
     * send audio and video but will not receive audio or video.
     */
    AudioSendVideoSend = AudioSend | VideoSend,
    /**
     * Join as a sending-audio-video-presentation-only participant, i.e. the
     * user will only send audio, video, and presentation but will not
     * receive audio, video, or presentation.
     */
    AudioSendVideoSendPresentationSend = AudioSendVideoSend | PresentationSend,
    /**
     * Join as a receiving-audio-video-only participant, i.e. the user will only
     * receive audio and video but will not send audio or video.
     */
    AudioRecvVideoRecv = AudioRecv | VideoRecv,
    /**
     * Join as a receiving-audio-video-presentation-only participant, i.e. the
     * user will only receive audio, video, and presentation but will not send
     * audio, video, or presentation.
     */
    AudioRecvVideoRecvPresentationRecv = AudioRecvVideoRecv | PresentationRecv,
    AudioRecvVideoRecvPresentationSendRecv = AudioRecvVideoRecv |
        PresentationSendRecv,
    AudioSendVideoSendPresentationSendRecv = AudioSendVideoSend |
        PresentationSendRecv,
    /**
     * Join as a full (send and receive) audio and presentation participant
     */
    AudioSendRecvPresentationSendRecv = AudioSendRecv | PresentationSendRecv,
    /**
     * Join as a full (send and receive) video and presentation participant
     */
    VideoSendRecvPresentationSendRecv = VideoSendRecv | PresentationSendRecv,
    /**
     * Join as a full (send and receive) audio and video participant
     */
    AudioSendRecvVideoSendRecv = AudioSendRecv | VideoSendRecv,
    /**
     * Join as a full (send and receive) audio, video, and presentation participant
     */
    AudioSendRecvVideoSendRecvPresentationSendRecv = AudioSendRecv |
        VideoSendRecv |
        PresentationSendRecv,

    AudioSendVideoRecv = AudioSend | VideoRecv,
    AudioRecvVideoSend = AudioRecv | VideoSend,
    AudioSendVideoRecvPresentationSendRecv = AudioSendVideoRecv |
        PresentationSendRecv,
    AudioRecvVideoSendPresentationSendRecv = AudioRecvVideoSend |
        PresentationSendRecv,
    AudioSendVideoSendPresentationRecv = AudioSendVideoSend | PresentationRecv,
    AudioRecvVideoRecvPresentationSend = AudioRecvVideoRecv | PresentationSend,
    AudioSendPresentationSend = AudioSend | PresentationSend,
    AudioSendPresentationRecv = AudioSend | PresentationRecv,
    AudioRecvPresentationSend = AudioRecv | PresentationSend,
    AudioRecvPresentationRecv = AudioRecv | PresentationRecv,
    VideoSendPresentationSend = VideoSend | PresentationSend,
    VideoSendPresentationRecv = VideoSend | PresentationRecv,
    VideoRecvPresentationSend = VideoRecv | PresentationSend,
    VideoRecvPresentationRecv = VideoRecv | PresentationRecv,
    AudioSendRecvVideoSendRecvPresentationSend = AudioSendRecv |
        VideoSendRecv |
        PresentationSend,
    AudioSendRecvVideoSendRecvPresentationRecv = AudioSendRecv |
        VideoSendRecv |
        PresentationRecv,

    // Complete list of 64 combinations (bitmask from 0 to 63)
    // Already defined above, but for completeness, here are all possible combinations:
    AudioSendVideoRecvPresentationSend = AudioSend |
        VideoRecv |
        PresentationSend,
    AudioSendVideoRecvPresentationRecv = AudioSend |
        VideoRecv |
        PresentationRecv,
    AudioRecvVideoSendPresentationSend = AudioRecv |
        VideoSend |
        PresentationSend,
    AudioRecvVideoSendPresentationRecv = AudioRecv |
        VideoSend |
        PresentationRecv,

    // All send/recv combinations for each media type
    AudioSendVideoSendRecv = AudioSend | VideoSendRecv,
    AudioSendRecvVideoSend = AudioSendRecv | VideoSend,
    AudioSendRecvVideoRecv = AudioSendRecv | VideoRecv,
    AudioSendVideoSendRecvPresentationSend = AudioSend |
        VideoSendRecv |
        PresentationSend,
    AudioSendVideoSendRecvPresentationRecv = AudioSend |
        VideoSendRecv |
        PresentationRecv,
    AudioSendRecvVideoSendPresentationSend = AudioSendRecv |
        VideoSend |
        PresentationSend,
    AudioSendRecvVideoSendPresentationRecv = AudioSendRecv |
        VideoSend |
        PresentationRecv,
    AudioSendRecvVideoRecvPresentationSend = AudioSendRecv |
        VideoRecv |
        PresentationSend,
    AudioSendRecvVideoRecvPresentationRecv = AudioSendRecv |
        VideoRecv |
        PresentationRecv,

    // All combinations with PresentationSendRecv
    AudioSendVideoSendRecvPresentationSendRecv = AudioSend |
        PresentationSendRecv |
        VideoSendRecv,
    AudioRecvVideoSendRecvPresentationSendRecv = AudioRecv |
        PresentationSendRecv |
        VideoSendRecv,
    AudioSendRecvVideoSendPresentationSendRecv = AudioSendRecv |
        PresentationSendRecv |
        VideoSend,
    AudioSendRecvVideoRecvPresentationSendRecv = AudioSendRecv |
        PresentationSendRecv |
        VideoRecv,

    // All combinations with AudioSendRecv
    AudioSendRecvPresentationSend = AudioSendRecv | PresentationSend,
    AudioSendRecvPresentationRecv = AudioSendRecv | PresentationRecv,

    // All combinations with VideoSendRecv
    VideoSendRecvPresentationSend = VideoSendRecv | PresentationSend,
    VideoSendRecvPresentationRecv = VideoSendRecv | PresentationRecv,

    AudioRecvVideoSendRecv = AudioRecv | VideoSendRecv,
    AudioRecvVideoSendRecvPresentationSend = AudioRecv |
        VideoSendRecv |
        PresentationSend,
    AudioRecvVideoSendRecvPresentationRecv = AudioRecv |
        VideoSendRecv |
        PresentationRecv,
}

export const CLIENT_CALL_TYPE_MAP = new Map<string, ClientCallType>(
    Object.entries(ClientCallType).flatMap(([k, v]) =>
        typeof v === 'number' ? [[k.toLowerCase(), v as ClientCallType]] : [],
    ),
);
